/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * Call requestIdleCallback in supported browsers.
 *
 * https://developers.google.com/web/updates/2015/08/using-requestidlecallback
 * http://caniuse.com/#feat=requestidlecallback
 */
export const requestIdleCallback = (fn) => {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(fn);
  } else {
    return setTimeout(() => fn(), 1);
  }
};

export const watchOnlineStatus = (callback, win = window) => {
  const off = () => callback(false);
  const on = () => callback(true);

  win.addEventListener('offline', off);
  win.addEventListener('online', on);
  const unsubscribe = () => {
    win.removeEventListener('offline', off);
    win.removeEventListener('online', on);
  };

  return unsubscribe;
};

/**
 * Performs a shallow comparison on two objects
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
export const shallowEquals = (a, b) => {
  for (let key in a) {
    if (!(key in b) || a[key] !== b[key]) {
      return false;
    }
  }
  for (let key in b) {
    if (!(key in a) || a[key] !== b[key]) {
      return false;
    }
  }

  return true;
};

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
export const noop = () => { };

/**
 * Flattens a tree data structure into an array.
 * @param {*} node
 * @returns
 */
export const flatten = (node, key = 'children') => {
  const children = (node[key] || []).reduce((a, b) => {
    return Array.isArray(b[key]) && !!b[key].length ?
      {...a, ...flatten(b, key)} :
      {...a, [b.id]: b};
  }, {});

  return {
    [node.id]: node,
    ...children,
  };
};

/**
 * Check the current execution environment
 * is client side or server side
 * @returns Boolean
 */
export const isServer = typeof window === 'undefined';

/**
 * retrieves an item from session storage
 * @param {string} key
 * @returns JSON | undefined
 */
export const getSessionJSONItem = (key) => {
  if (isServer) {
    return undefined;
  }
  const item = window.sessionStorage.getItem(key);

  if (item) {
    return JSON.parse(item);
  } else {
    return undefined;
  }
};

/**
 * sets an item in session storage
 * @param {string} key
 * @param {string} value
 */
export const setSessionJSONItem = (key, value) => {
  window.sessionStorage.setItem(key, JSON.stringify(value));
};

/**
 * clears an item in session storage
 * @param {string} key
 */
export const clearSessionJSONItem = (key) => {
  window.sessionStorage.removeItem(key);
};

/**
 * escapes special regex characters
 * @param {string} str
 * @returns escaped string
 */
export const escapeRegexChars = (str) => str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

/**
 * bolds a substring of a string by adding <b> tags
 * @param {string} str
 * @param {string} substr
 * @returns stringified HTML Node
 */
export const boldString = (str, substr) => {
  return str?.replace(RegExp(escapeRegexChars(substr.trim()), 'gi'), '<b>$&</b>');
};

/**
 * Capitalizes the words in a string
 * @param {string} text
 * @returns capitalized text
 */
export const capitalize = (text) => {
  return text
    .toLowerCase()
    .split(' ')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
};

const safeToCamel = (str) => {
  if (str.startsWith('_') || str.startsWith('c_')) {
    return str;
  }

  return str.replace(/([-_][a-z])/gi, ($1) => {
    return $1[1].toUpperCase();
  });
};

const isPlainObject = (obj) => {
  return obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function';
};

export const keysToCamel = (obj) => {
  if (isPlainObject(obj)) {
    const n = {};

    Object.keys(obj).forEach((k) => {
      n[safeToCamel(k)] = keysToCamel(obj[k]);
    });

    return n;
  } else if (Array.isArray(obj)) {
    return obj.map(keysToCamel);
  }

  return obj;
};

/**
 * Merge two arrays (arr1 and arr2) into one array
 * and merge the matched items (objects with the same id)
 * into one object. There is an assumption where arr2 is
 * always a subset of arr1.
 *
 * @param arr1 - array of objects
 * @param arr2
 * @return {Array}
 */
export const mergeMatchedItems = (arr1 = [], arr2 = []) => {
  const merged = arr1.map((item) => {
    const match = arr2.find((item2) => item2.id === item.id);

    return match ? {...item, ...match} : item;
  });

  return merged;
};

/**
 * This util func determines if the app is finished hydration
 * @return {boolean}
 */
export const isHydrated = () => typeof window !== 'undefined' && !window.__HYDRATING__;

/**
 * Constructs a redirectURI by combining `appOrigin` with `redirectPath`.
 * Ensures that `redirectPath` starts with a '/'.
 * Returns an empty string if `redirectPath` is falsy.
 *
 * @param {*} appOrigin
 * @param {*} redirectPath - relative redirect path
 * @returns redirectURI to be passed into the social login flow
 */
export const buildRedirectURI = (appOrigin = '', redirectPath = '') => {
  if (redirectPath) {
    const path = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;

    return `${appOrigin}${path}`;
  } else {
    return '';
  }
};
